import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { cartographyApi } from 'src/Services/API';
import {
  trackBedAdded,
  trackBedRemoved,
  trackBuildingCreated,
  trackFloorCreated,
  trackRoomCreated,
  trackRoomSetOutOfService,
} from 'src/Services/Mixpanel/events';
import {
  BedDto,
  BuildingDto,
  CreateBedDto,
  CreateBuildingDto,
  CreateFloorDto,
  CreateRoomDto,
  FloorDto,
  RoomDto,
  UpdateBedDto,
  UpdateBuildingDto,
  UpdateFloorDto,
  UpdateRoomDto,
} from 'src/types/cartography.types';

const invalidateCartography = (
  queryClient: ReturnType<typeof useQueryClient>,
  assetId?: string
) => {
  queryClient.invalidateQueries({ queryKey: ['fetch-cartography-tree', assetId] });
  queryClient.invalidateQueries({ queryKey: ['fetch-cartography-summary', assetId] });
};

// ============================================================
// Buildings
// ============================================================

export const useCreateBuilding = (
  options?: UseMutationOptions<BuildingDto, unknown, CreateBuildingDto>
) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<BuildingDto, unknown, CreateBuildingDto>(
    ['create-building', assetId],
    async payload => {
      if (!assetId) throw new Error('No asset selected');
      return cartographyApi.createBuilding(assetId, payload);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        trackBuildingCreated({ building_type: data.type ?? 'UNSPECIFIED' });
        invalidateCartography(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

export const useUpdateBuilding = (
  options?: UseMutationOptions<
    BuildingDto,
    unknown,
    { buildingId: string; payload: UpdateBuildingDto }
  >
) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<BuildingDto, unknown, { buildingId: string; payload: UpdateBuildingDto }>(
    ['update-building', assetId],
    async ({ buildingId, payload }) => {
      if (!assetId) throw new Error('No asset selected');
      return cartographyApi.updateBuilding(assetId, buildingId, payload);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidateCartography(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

export const useDeleteBuilding = (options?: UseMutationOptions<void, unknown, string>) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>(
    ['delete-building', assetId],
    async buildingId => {
      if (!assetId) throw new Error('No asset selected');
      await cartographyApi.deleteBuilding(assetId, buildingId);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidateCartography(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

// ============================================================
// Floors
// ============================================================

export const useCreateFloor = (
  options?: UseMutationOptions<FloorDto, unknown, { buildingId: string; payload: CreateFloorDto }>
) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<FloorDto, unknown, { buildingId: string; payload: CreateFloorDto }>(
    ['create-floor', assetId],
    async ({ buildingId, payload }) => {
      if (!assetId) throw new Error('No asset selected');
      return cartographyApi.createFloor(assetId, buildingId, payload);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        trackFloorCreated({ level: data.level });
        invalidateCartography(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

export const useUpdateFloor = (
  options?: UseMutationOptions<FloorDto, unknown, { floorId: string; payload: UpdateFloorDto }>
) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<FloorDto, unknown, { floorId: string; payload: UpdateFloorDto }>(
    ['update-floor', assetId],
    async ({ floorId, payload }) => {
      if (!assetId) throw new Error('No asset selected');
      return cartographyApi.updateFloor(assetId, floorId, payload);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidateCartography(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

export const useDeleteFloor = (options?: UseMutationOptions<void, unknown, string>) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>(
    ['delete-floor', assetId],
    async floorId => {
      if (!assetId) throw new Error('No asset selected');
      await cartographyApi.deleteFloor(assetId, floorId);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidateCartography(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

// ============================================================
// Rooms
// ============================================================

export const useCreateRoom = (options?: UseMutationOptions<RoomDto, unknown, CreateRoomDto>) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<RoomDto, unknown, CreateRoomDto>(
    ['create-room', assetId],
    async payload => {
      if (!assetId) throw new Error('No asset selected');
      return cartographyApi.createRoom(assetId, payload);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        trackRoomCreated({ room_type: data.type });
        invalidateCartography(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

export const useUpdateRoom = (
  options?: UseMutationOptions<RoomDto, unknown, { roomId: string; payload: UpdateRoomDto }>
) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<RoomDto, unknown, { roomId: string; payload: UpdateRoomDto }>(
    ['update-room', assetId],
    async ({ roomId, payload }) => {
      if (!assetId) throw new Error('No asset selected');
      return cartographyApi.updateRoom(assetId, roomId, payload);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        if (variables.payload.isOutOfService === true) {
          trackRoomSetOutOfService({ room_type: data.type });
        }
        invalidateCartography(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

export const useDeleteRoom = (options?: UseMutationOptions<void, unknown, string>) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>(
    ['delete-room', assetId],
    async roomId => {
      if (!assetId) throw new Error('No asset selected');
      await cartographyApi.deleteRoom(assetId, roomId);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidateCartography(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

// ============================================================
// Beds
// ============================================================

export const useCreateBed = (
  options?: UseMutationOptions<BedDto, unknown, { roomId: string; payload: CreateBedDto }>
) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<BedDto, unknown, { roomId: string; payload: CreateBedDto }>(
    ['create-bed', assetId],
    async ({ roomId, payload }) => {
      if (!assetId) throw new Error('No asset selected');
      return cartographyApi.createBed(assetId, roomId, payload);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        trackBedAdded({ bed_type: data.type });
        invalidateCartography(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

export const useUpdateBed = (
  options?: UseMutationOptions<BedDto, unknown, { bedId: string; payload: UpdateBedDto }>
) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<BedDto, unknown, { bedId: string; payload: UpdateBedDto }>(
    ['update-bed', assetId],
    async ({ bedId, payload }) => {
      if (!assetId) throw new Error('No asset selected');
      return cartographyApi.updateBed(assetId, bedId, payload);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidateCartography(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

export const useDeleteBed = (options?: UseMutationOptions<void, unknown, string>) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>(
    ['delete-bed', assetId],
    async bedId => {
      if (!assetId) throw new Error('No asset selected');
      await cartographyApi.deleteBed(assetId, bedId);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        trackBedRemoved();
        invalidateCartography(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};
